import React, { useEffect, useState } from "react";
import styled from "styled-components";
import * as Ariakit from "@ariakit/react";
import { Select, SelectItem, SelectProvider } from "../form/select";
import { BE_BASE_URL } from "../../utils/config";
import { Color } from "../../constants/color";
import { axiosInstance, useAuth } from "../../provider/authProvider";
import { avvisoUnico } from "../../features/notifiche/avvisi";
import { useTranslation } from "react-i18next";
import Suggerimento from "../tooltip/suggerimento";
import { useSolaLettura } from "../../features/user/solaLettura";

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  padding: 2rem 4rem;
  width: 65rem;
  max-width: 100%;
  margin: 0 auto;
  background-color: var(--white);
  border-radius: 1rem;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  padding: 0 2rem;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-family: "Avenir Next", sans-serif;
    font-weight: bold;
    color: ${Color.LIGHT_BLUE};
  }

  input,
  textarea {
    border: 1px solid ${Color.LIGHT_BLUE};
    border-radius: 1rem;
    padding: 0.5rem 1rem;
    width: 100%;
    font-size: 1rem;
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

const RequestLabel = styled.p`
  font-family: "Avenir Next", sans-serif;
  font-weight: bold;
  color: ${Color.LIGHT_BLUE};
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${Color.WHITE};
  background-color: ${Color.BLUE};
  padding: 0.8em 2em;
  border-radius: 18px;
  border-style: hidden;
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  align-self: center;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/*
 Un solo avviso per la richiesta di corso dedicato: la conferma dell'invio prende il posto
 dell'eventuale avviso di campi mancanti, invece di affiancarlo.
*/
const AVVISO_RICHIESTA = "prenota-corso-sede";

const PrenotaCorsoSede: React.FC = () => {
  const { t } = useTranslation();
  // In sola lettura (abbonamento disattivato, ODOO 300) non si richiedono corsi: il backend
  // rifiuterebbe la richiesta.
  const solaLettura = useSolaLettura();
  const { token } = useAuth();
  const [sedeOptions, setSedeOptions] = useState<Map<string, string>>(
    new Map()
  );
  const [corsoOptions, setCorsoOptions] = useState<Map<string, string>>(
    new Map()
  );

  const [sede, setSede] = useState<string>(""); // Stores the selected sede ID
  const [sedeLabel, setSedeLabel] = useState<string>(""); // Stores the selected sede label

  const [corso, setCorso] = useState<string>(""); // Stores the selected corso ID
  const [corsoLabel, setCorsoLabel] = useState<string>(""); // Stores the selected corso label

  const [numeroDipendenti, setNumeroDipendenti] = useState<number | "">("");
  const [descrizione, setDescrizione] = useState("");

  const sedeStore = Ariakit.useSelectStore({
    value: sedeLabel, // Show the label in the dropdown
    setValue: (label) => {
      setSedeLabel(label);
      // Find the ID corresponding to the label selected
      const selectedSedeId =
        Array.from(sedeOptions.entries()).find(
          ([, value]) => value === label
        )?.[0] || "";
      setSede(selectedSedeId);
    },
  });

  const corsoStore = Ariakit.useSelectStore({
    value: corsoLabel, // Show the label in the dropdown
    setValue: (label) => {
      setCorsoLabel(label);
      // Find the ID corresponding to the label selected
      const selectedCorsoId =
        Array.from(corsoOptions.entries()).find(
          ([, value]) => value === label
        )?.[0] || "";
      setCorso(selectedCorsoId);
    },
  });

  useEffect(() => {
    async function fetchSedi() {
      try {
        const response = await axiosInstance.get(
          `${BE_BASE_URL}/courses/officesInfo?cp=true`
        );
        const data: { id: string; office: string }[] = response.data;
        const optionsMap = new Map(data.map(({ id, office }) => [id, office]));
        setSedeOptions(optionsMap);
      } catch (error) {
        //console.error("Error fetching offices info:", error);
      }
    }

    async function fetchCorsi() {
      try {
        const response = await axiosInstance.get(
          `${BE_BASE_URL}/courses/packageInfo?cp=true`
        );
        const data: { id: string; course: string }[] = response.data;
        const optionsMap = new Map(data.map(({ id, course }) => [id, course]));
        setCorsoOptions(optionsMap);
      } catch (error) {
        //console.error("Error fetching course info:", error);
      }
    }

    fetchSedi();
    fetchCorsi();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (solaLettura) {
      return;
    }

    if (!sede || !corso) {
      avvisoUnico(
        AVVISO_RICHIESTA,
        "warning",
        "Seleziona una sede e un corso per inviare la richiesta."
      );
      return;
    }

    const formData = {
      officeId: sede,
      courseId: corso,
      employeesNum: numeroDipendenti,
      description: descrizione,
    };

    try {
      // Send the form data to the backend via a POST request
      const response = await axiosInstance.post(
        `${BE_BASE_URL}/notifications/create`,
        formData
      );
      if (response.status === 200 || response.status === 201) {
        // console.log("Form submitted successfully:", response.data);
        avvisoUnico(AVVISO_RICHIESTA, "success", "Notifica inviata con successo!");
        // Reset form fields
        resetForm();
      } else {
        //console.error("Unexpected response status:", response.status);
      }
    } catch (error) {
      //console.error("Error submitting the form:", error);
    }
  };

  // New function to reset the form
  const resetForm = () => {
    setSede("");
    setSedeLabel("");
    setCorso("");
    setCorsoLabel("");
    setNumeroDipendenti("");
    setDescrizione("");
    // Reset the select stores
    sedeStore.setValue("");
    corsoStore.setValue("");
  };

  return (
    <Layout>
      <FormContainer onSubmit={handleSubmit}>
        <RequestLabel>Inserisci la tua richiesta</RequestLabel>
        {/* SEDE Dropdown */}
        {/*
         In ordine alfabetico, come le altre tendine. Le voci vuote, i doppioni esatti e lo
         stesso luogo scritto in due modi arrivano cosi' da `courses/officesInfo` e
         `courses/packageInfo`: sono dati da bonificare alla fonte, e nasconderli qui li
         renderebbe solo piu' difficili da trovare.
        */}
        <FormField>
          <label htmlFor="sede">SEDE</label>
          <SelectProvider store={sedeStore}>
            <Select id="sede">
              <SelectItem key="" value="" disabled>
                Seleziona una sede
              </SelectItem>
              {Array.from(sedeOptions.entries())
                .sort(([, a], [, b]) => a.localeCompare(b))
                .map(([key, value]) => (
                  <SelectItem key={`sede_${key}`} value={value}>
                    {value}
                  </SelectItem>
                ))}
            </Select>
          </SelectProvider>
        </FormField>

        {/* CORSO Dropdown */}
        <FormField>
          <label htmlFor="corso">CORSO</label>
          <SelectProvider store={corsoStore}>
            <Select id="corso">
              <SelectItem key="" value="" disabled>
                Seleziona un corso
              </SelectItem>
              {Array.from(corsoOptions.entries())
                .sort(([, a], [, b]) => a.localeCompare(b))
                .map(([key, value]) => (
                  <SelectItem key={`corso_${key}`} value={value}>
                    {value}
                  </SelectItem>
                ))}
            </Select>
          </SelectProvider>
        </FormField>

        {/* NUMERO DIPENDENTI */}
        <FormField>
          <label htmlFor="numeroDipendenti">NUMERO DIPENDENTI</label>
          <input
            type="number"
            id="numeroDipendenti"
            name="numeroDipendenti"
            min="1"
            value={numeroDipendenti}
            onChange={(e) =>
              setNumeroDipendenti(
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
            required
          />
          <p>
            Si ricorda che come da contratto per i corsi dedicati il numero
            minimo di partecipanti è 10. Laddove la normativa prevede un numero
            inferiore la soglia minima è pari all&#39;80% della capienza massima
            arrotondata per eccesso.
          </p>
        </FormField>

        {/* DESCRIZIONE */}
        <FormField>
          <label htmlFor="descrizione">DESCRIZIONE</label>
          <textarea
            id="descrizione"
            name="descrizione"
            value={descrizione}
            onChange={(e) => setDescrizione(e.target.value)}
            required
          />
        </FormField>
        {/* Submit Button */}
        <Suggerimento
          testo={t(solaLettura ? "sola-lettura-comando" : "attestati-aiuto-invia-richiesta")}
          style={{ alignSelf: "center" }}
        >
          <SubmitButton type="submit" disabled={solaLettura}>
            Invia richiesta
          </SubmitButton>
        </Suggerimento>
      </FormContainer>
    </Layout>
  );
};

export default PrenotaCorsoSede;
